import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Col from 'react-bootstrap/esm/Col';
import Row from 'react-bootstrap/esm/Row'; 
import ProgressBar from 'react-bootstrap/ProgressBar';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import '../pages/card/card.css'

const IMAGE_TYPE_OPTIONS = [
    { value: 'grader_front_url', label: 'Grader Front' },
    { value: 'grader_back_url', label: 'Grader Back' },
    { value: 'scan_front_url', label: 'Scan Front' },
    { value: 'scan_back_url', label: 'Scan Back' },
    { value: 'alientrader_front_url', label: 'Alien Trader Front' },
    { value: 'alientrader_back_url', label: 'Alien Trader Back' },
    { value: 'misc_image_url', label: 'Miscellaneous 1' },
    { value: 'misc_image2_url', label: 'Miscellaneous 2' },
    { value: 'misc_image3_url', label: 'Miscellaneous 3' },
];

const MULTI_UPLOAD_FIELDS = new Set([
    'misc_image_url',
    'misc_image2_url',
    'misc_image3_url',
]);

const clearPrimaryImageFieldsBeforeUpload = async (cardId, uploadedFilesByField) => {
    const fieldsToClear = Object.keys(uploadedFilesByField).filter(
        (fieldName) => !MULTI_UPLOAD_FIELDS.has(fieldName)
    );

    if (fieldsToClear.length === 0) {
        return;
    }

    const replacementPayload = {};
    fieldsToClear.forEach((fieldName) => {
        replacementPayload[fieldName] = null;
    });

    await axios.post(`https://api.alientrader.cards/edit/card/${cardId}`, replacementPayload);
};

const ImageUploadComponent = (props) => {
    const [files, setFiles] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [warningMessage, setWarningMessage] = useState('');
    const [hoveredRemoveFileId, setHoveredRemoveFileId] = useState('');
    const [isChooseFilesHovered, setIsChooseFilesHovered] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(() =>
        typeof window !== 'undefined' ? window.innerWidth < 768 : false
    );
    const fileInputRef = useRef(null);
    const filesRef = useRef([]);

    const getDuplicatePrimaryAssignments = (entries) => {
        const assignmentCounts = entries.reduce((counts, entry) => {
            if (!MULTI_UPLOAD_FIELDS.has(entry.fieldName)) {
                counts[entry.fieldName] = (counts[entry.fieldName] || 0) + 1;
            }
            return counts;
        }, {});

        return Object.entries(assignmentCounts)
            .filter(([, count]) => count > 1)
            .map(([fieldName]) => {
                const option = IMAGE_TYPE_OPTIONS.find((item) => item.value === fieldName);
                return option ? option.label : fieldName;
            });
    };

    useEffect(() => {
        filesRef.current = files;
    }, [files]);

    useEffect(() => {
        const duplicateTargets = getDuplicatePrimaryAssignments(files);

        if (duplicateTargets.length > 0) {
            setWarningMessage(`Only one upload is allowed for ${duplicateTargets.join(', ')}. Use Miscellaneous for multiple images.`);
            return;
        }

        setWarningMessage('');
    }, [files]);

    useEffect(() => {
        const handleResize = () => {
            setIsSmallScreen(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        return () => {
            filesRef.current.forEach((entry) => {
                if (entry.previewUrl) {
                    URL.revokeObjectURL(entry.previewUrl);
                }
            });
        };
    }, []);

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files || []);

        setFiles((prevFiles) => [
            ...prevFiles,
            ...selectedFiles.map((file, index) => ({
                id: `${file.name}-${file.lastModified}-${prevFiles.length + index}`,
                file,
                fieldName: 'grader_front_url',
                previewUrl: URL.createObjectURL(file),
            })),
        ]);

        e.target.value = '';
    };

    const handleFieldChange = (fileId, fieldName) => {
        setFiles((prevFiles) =>
            prevFiles.map((entry) =>
                entry.id === fileId ? { ...entry, fieldName } : entry
            )
        );
    };

    const handleRemoveFile = (fileId) => {
        setFiles((prevFiles) => {
            const fileToRemove = prevFiles.find((entry) => entry.id === fileId);
            if (fileToRemove?.previewUrl) {
                URL.revokeObjectURL(fileToRemove.previewUrl);
            }

            return prevFiles.filter((entry) => entry.id !== fileId);
        });
    };

    const handleUpload = async () => {
        if (!files.length) {
            return;
        }

        const duplicateTargets = getDuplicatePrimaryAssignments(files);
        if (duplicateTargets.length > 0) {
            setWarningMessage(`Only one upload is allowed for ${duplicateTargets.join(', ')}. Use Miscellaneous for multiple images.`);
            return;
        }

        const formData = new FormData();
        setUploading(true);
        setUploadProgress(0);

        const uploadedFilesByField = files.reduce((grouped, { fieldName, file }) => {
            if (!grouped[fieldName]) {
                grouped[fieldName] = [];
            }

            grouped[fieldName].push(file.name);
            return grouped;
        }, {});

        files.forEach(({ fieldName, file }) => {
            formData.append(fieldName, file);
        });

        try {
            await clearPrimaryImageFieldsBeforeUpload(props.cardId, uploadedFilesByField);

            const response = await axios.post(`https://api.alientrader.cards/card/upload/${props.cardId}`, formData, {
                onUploadProgress: (progressEvent) => {
                    let percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percentCompleted);
                }
            });

            console.log(response.data);
            props.onUrlsReceived(response.data); 
            files.forEach((entry) => {
                if (entry.previewUrl) {
                    URL.revokeObjectURL(entry.previewUrl);
                }
            });
            setFiles([]);
            setWarningMessage('');
        } catch (error) {
            console.error("Error uploading files:", error);
            setWarningMessage(
                error?.response?.data?.message ||
                error?.response?.data ||
                'Unable to upload these images.'
            );
        } finally {
            setUploading(false);
        }
    };

    return (
        <Row className='justify-content-center'>
        <Row className='justify-content-center'>
                <button
                    onClick={handleUpload}
                    className='uploadButton'
                    style={{ width: '200px', maxWidth: '100%' }}
                >
                    Upload
                </button>
            </Row>
            <Row className='uploadProgressRow'>
                {uploading && <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} />}
            </Row>
            <Row className='justify-content-center' style={{ marginTop: '8px' }}>
                <Col
                    md={11}
                    lg={10}
                    xl={9}
                    className='custom-card-box shadow-sm'
                    style={{
                        width: '100%',
                        maxWidth: isSmallScreen ? '100%' : '680px',
                    }}
                >
                    <Row>
                        <div className='custom-info-header'>Upload Images</div>
                    </Row>
                    <Row className='card-images'>
                        <Col>
                            <Row
                                className='justify-content-center'
                                style={{ padding: '12px', textAlign: 'center' }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        width: '100%',
                                    }}
                                >
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        onChange={handleFileChange}
                                        style={{ display: 'none' }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        onMouseEnter={() => setIsChooseFilesHovered(true)}
                                        onMouseLeave={() => setIsChooseFilesHovered(false)}
                                        style={{
                                            border: `1px solid ${isChooseFilesHovered ? '#ff3030' : 'rgba(0,0,0,0.18)'}`,
                                            backgroundColor: isChooseFilesHovered ? '#ff3030' : '#ffffff',
                                            color: isChooseFilesHovered ? '#ffffff' : '#111111',
                                            borderRadius: '10px',
                                            padding: '8px 14px',
                                            fontSize: '15px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px',
                                            transition: 'all 0.15s ease',
                                        }}
                                    >
                                        <AddPhotoAlternateIcon fontSize="small" />
                                        Choose Files
                                    </button>
                                </div>
                            </Row>
                            <Row style={{ padding: '12px' }}>
                                <div className='custom-info-misc' style={{ marginBottom: '12px' }}>
                                    Select multiple files, then choose what each image should update before uploading.
                                </div>
                                {warningMessage && (
                                    <div
                                        className='custom-info-misc'
                                        style={{
                                            width: '100%',
                                            marginBottom: '12px',
                                            color: '#8a2323',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {warningMessage}
                                    </div>
                                )}
                                {files.length === 0 ? (
                                    <div className='custom-info-misc'>No files selected yet.</div>
                                ) : (
                                    files.map((entry) => (
                                        <div
                                            key={entry.id}
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: '14px',
                                                marginBottom: isSmallScreen ? '8px' : '16px',
                                                width: '100%',
                                                padding: isSmallScreen ? '6px 0 10px' : '10px 0',
                                                borderBottom: '1px solid rgba(0,0,0,0.08)',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: '10px',
                                                    minWidth: 'auto',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        position: 'relative',
                                                        width: isSmallScreen ? '88px' : '72px',
                                                        height: isSmallScreen ? '88px' : '72px',
                                                    }}
                                                >
                                                    <img
                                                        src={entry.previewUrl}
                                                        alt={entry.file.name}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            borderRadius: '10px',
                                                            border: '1px solid rgba(0,0,0,0.12)',
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveFile(entry.id)}
                                                        onMouseEnter={() => setHoveredRemoveFileId(entry.id)}
                                                        onMouseLeave={() => setHoveredRemoveFileId('')}
                                                        style={{
                                                            position: 'absolute',
                                                            top: '50%',
                                                            left: '50%',
                                                            transform: 'translate(-50%, -50%)',
                                                            border: 'none',
                                                            backgroundColor: hoveredRemoveFileId === entry.id ? '#ff3030' : 'rgba(0, 0, 0, 0.55)',
                                                            color: '#ffffff',
                                                            width: '30px',
                                                            height: '30px',
                                                            borderRadius: '999px',
                                                            fontSize: '16px',
                                                            lineHeight: '30px',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: 'pointer',
                                                            transition: 'background-color 0.15s ease',
                                                        }}
                                                        title="Remove image"
                                                        aria-label="Remove image"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                                {isSmallScreen && (
                                                    <div
                                                        style={{
                                                            marginTop: '2px',
                                                            maxWidth: '220px',
                                                            textAlign: 'center',
                                                            fontWeight: 600,
                                                            fontSize: '13px',
                                                            wordBreak: 'break-word',
                                                        }}
                                                    >
                                                        {entry.file.name}
                                                    </div>
                                                )}
                                            </div>
                                            <div
                                                style={{
                                                    flex: '0 0 auto',
                                                    width: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                {!isSmallScreen && (
                                                    <div
                                                        style={{
                                                            marginBottom: '8px',
                                                            fontWeight: 600,
                                                            textAlign: 'center',
                                                            wordBreak: 'break-word',
                                                        }}
                                                    >
                                                        {entry.file.name}
                                                    </div>
                                                )}
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: isSmallScreen ? '6px' : '8px',
                                                        justifyContent: 'center',
                                                        padding: isSmallScreen ? '10px 8px 0' : '0',
                                                        backgroundColor: isSmallScreen ? '#f8faf4' : 'transparent',
                                                        borderRadius: isSmallScreen ? '12px' : '0',
                                                        border: isSmallScreen ? '1px solid rgba(0,0,0,0.08)' : 'none',
                                                        width: isSmallScreen ? 'fit-content' : '100%',
                                                        maxWidth: isSmallScreen ? '100%' : '520px',
                                                    }}
                                                >
                                                    {IMAGE_TYPE_OPTIONS.map((option) => {
                                                        const isSelected = entry.fieldName === option.value;

                                                        return (
                                                            <button
                                                                key={option.value}
                                                                type="button"
                                                                onClick={() => handleFieldChange(entry.id, option.value)}
                                                                style={{
                                                                    border: isSelected ? '1px solid #1f1f1f' : '1px solid rgba(0,0,0,0.12)',
                                                                    backgroundColor: isSelected ? '#e7f36b' : '#ffffff',
                                                                    color: '#1f1f1f',
                                                                    borderRadius: '999px',
                                                                    padding: isSmallScreen ? '5px 8px' : '7px 10px',
                                                                    fontSize: isSmallScreen ? '11px' : '12px',
                                                                    fontWeight: isSelected ? 700 : 500,
                                                                    cursor: 'pointer',
                                                                }}
                                                            >
                                                                {option.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </Row>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row className='uploadProgressRow'>
                {uploading && <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} />}
            </Row>
            <Row className='justify-content-center'>
                <button
                    onClick={handleUpload}
                    className='uploadButton'
                    style={{ width: '200px', maxWidth: '100%' }}
                >
                    Upload
                </button>
            </Row>
        </Row>
    );
};

export default ImageUploadComponent;
